import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApiClient } from "./api-context.js";

export interface OperatorCostSummary {
  needsCostsCount: number;
  historicalNeedsCostsCount: number;
  needsCostsRevenue?: number;
  historicalRevenueWithoutCosts: number;
  needsCostsFirstSoldAt?: string | null;
  needsCostsLastSoldAt?: string | null;
  historicalFirstSoldAt?: string | null;
  historicalLastSoldAt?: string | null;
  currency?: string;
  timeZone?: string;
  isSample?: boolean;
}

/** Only rendered after the account's operator role is confirmed by AppLayout. */
export function OperatorCostNotice({ accountId }: { accountId: string }): React.ReactElement | null {
  const api = useApiClient();
  const query = useQuery({
    queryKey: ["action-items", accountId],
    queryFn: () => api.get<OperatorCostSummary>("/dashboard/action-items"),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  return <OperatorCostNoticeCard accountId={accountId} data={query.data} failed={query.isError} onRetry={() => void query.refetch()} />;
}

function recordedPeriod(from: string | null | undefined, to: string | null | undefined, timeZone: string): string {
  if (!from || !to) return "across the imported history";
  const format = (value: string): string => new Intl.DateTimeFormat("en-US", {
    month: "short", day: "numeric", year: "numeric", timeZone,
  }).format(new Date(value));
  const start = format(from);
  const end = format(to);
  return start === end ? `on ${start}` : `from ${start} to ${end}`;
}

export function OperatorCostNoticeCard({ accountId, data, failed, onRetry }: {
  accountId: string;
  data?: OperatorCostSummary;
  failed: boolean;
  onRetry: () => void;
}): React.ReactElement | null {
  // Never change the public demo or the resettable onboarding test experience.
  if (accountId === "11111111-1111-4111-8111-111111111111"
    || accountId === "22222222-2222-4222-8222-222222222222" || data?.isSample) return null;
  if (!failed && (!data || (data.needsCostsCount === 0 && data.historicalNeedsCostsCount === 0))) return null;
  // An older API may not yet send the currency. Keep the counts useful, but
  // never label an unknown amount as dollars during a rolling deployment.
  const money = (value: number): string => new Intl.NumberFormat("en-US", {
    style: "currency", currency: data?.currency, maximumFractionDigits: 0,
  }).format(value);
  const timeZone = data?.timeZone ?? "UTC";
  return (
    <section data-operator-cost-notice="true" role="status" style={noticeStyle}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--body)", marginBottom: 4 }}>Agency team only</div>
      <strong style={{ color: "var(--text)", fontSize: 14 }}>
        {failed ? "The missing-cost check could not refresh" : "Needs attention: cost inputs are missing"}
      </strong>
      {failed && <p style={bodyStyle}>Refresh this check before treating the cost list as current. This does not mean the costs are complete.</p>}
      {data && <div style={{ display: "grid", gap: 6, marginTop: 7 }}>
        {data.needsCostsCount > 0 && <p style={bodyStyle}>
          <b>{data.needsCostsCount} active catalog {data.needsCostsCount === 1 ? "entry needs" : "entries need"} costs.</b>{" "}
          These are SKU/offer entries with recorded sales {recordedPeriod(data.needsCostsFirstSoldAt, data.needsCostsLastSoldAt, timeZone)}.
          {data.currency && typeof data.needsCostsRevenue === "number" && <> Recorded product sales: {money(data.needsCostsRevenue)}.</>}
        </p>}
        {data.historicalNeedsCostsCount > 0 && <p style={bodyStyle}>
          <b>{data.historicalNeedsCostsCount} discontinued {data.historicalNeedsCostsCount === 1 ? "entry needs" : "entries need"} historical costs.</b>{" "}
          Recorded sales {recordedPeriod(data.historicalFirstSoldAt, data.historicalLastSoldAt, timeZone)}.
          {data.currency && <> Recorded product sales without those costs: {money(data.historicalRevenueWithoutCosts)}.</>}
        </p>}
        {(data.needsCostsCount > 0 || data.historicalNeedsCostsCount > 0) && <p style={{ ...bodyStyle, color: "var(--body-faint)", fontSize: 11.5 }}>
          All imported history, not the selected dates. Entries are not product-family counts or proof of recent sales.
          {" "}Sales are after line discounts, before refunds, and exclude tax and shipping. These amounts are not lost profit.
        </p>}
      </div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 10, fontSize: 12.5, fontWeight: 700 }}>
        <Link to="/costs#missing-costs" style={linkStyle}>Review missing costs →</Link>
        <Link to={`/agency/data-health?account=${encodeURIComponent(accountId)}`} style={linkStyle}>Open this account’s data checks →</Link>
        {failed && <button type="button" onClick={onRetry} style={{ ...linkStyle, border: 0, padding: 0, background: "none", font: "inherit", cursor: "pointer" }}>Retry check</button>}
      </div>
    </section>
  );
}

const noticeStyle: React.CSSProperties = {
  marginBottom: 16, padding: "13px 15px", borderRadius: "var(--r-sm)",
  border: "1px solid color-mix(in srgb, var(--warn, #E5A83B) 50%, var(--border))",
  background: "color-mix(in srgb, var(--warn, #E5A83B) 7%, var(--surface))",
};
const bodyStyle: React.CSSProperties = { margin: 0, fontSize: 12.5, lineHeight: 1.5, color: "var(--body)" };
const linkStyle: React.CSSProperties = { color: "var(--accent)", textDecoration: "none" };
